import React from 'react'
import { FormTypes, SubmissionTypes } from '@oneblink/types'
import {
  submissionService,
  OneBlinkAppsError,
  SetFormSubmission,
} from '@oneblink/apps-react'
import { ExecutedLookups } from '@oneblink/apps-react'
import { toast } from 'react-toastify'
import _cloneDeep from 'lodash.clonedeep'
import jsum from 'jsum'

import LoadingSpinner from '../../../components/LoadingSpinner'
import Dialog from '../../../components/Dialog'

import useSubmitForm from '../../../hooks/useSubmitForm'
import useCancelForm from '../../../hooks/useCancelForm'
import useSaveDraft from '../../../hooks/useSaveDraft'
import useCheckMaintenanceOnSubmit from '../../../hooks/useCheckMaintenanceOnSubmit'
import MaintenanceModal from '../../../components/MaintenanceModel'
import useAutoSaveApprovalsControlledForm from '../../../hooks/useAutoSaveApprovalsControlledForm'
import AutoSaveApprovalsControlledForm from '../../../components/AutoSaveApprovalsControlledForm'

import {
  populateContactElements,
  personCompletingFormDetails,
  avokaContactReferenceDetails,
  emergencyContact,
  updateEmergencyContactIsMobile,
} from '../../../lib/controlled-forms/food-licensing/contact'
import { populateBusinessContactsEmail } from '../../../lib/controlled-forms/common/contacts'
import {
  addClass,
  removeClass,
} from '../../../lib/controlled-forms/common/classes'
import { generateLicenceChargesTable } from '../../../lib/controlled-forms/food-licensing/licence-charges'
import {
  updateSpinnerState,
  setSpinnerInitial,
  setSpinnerError,
} from '../../../lib/controlled-forms/food-licensing/loading-spinner'
import { findElement } from '../../../lib/definition-manipulation'
import isDeepEqual from '../../../lib/is-deep-equal'
import {
  setHasPricingLookupRunInitial,
  updateHasPricingLookupRun,
  verifyPricingLookupHasRun,
} from '../../../lib/controlled-forms/common/has-pricing-lookup-run'
import getPricingAndCrn from '../../../lib/controlled-forms/common/get-pricing-and-crn'
import {
  generateFacilityLineItems,
  generateApplicationFeeLineItem,
  generateProductLineItems,
} from '../../../lib/controlled-forms/common/generate-invoice-line-items'

import { HTTPError } from '../../../lib/http'

import getPricing from '../../../lib/controlled-forms/common/get-pricing'
import updateFacilityLabels from '../../../lib/controlled-forms/food-licensing/facility-names'
import serverValidation from '../../../lib/server-validation'
import { useAppConfig } from '../../../providers/AppConfigProvider'
import {
  getPointAddressPostalDeliveryType,
  SubmissionPointAddress,
} from '../../../lib/point-address'

interface FormProps {
  form: FormTypes.Form
  preFillData: Record<string, unknown>
}

function generatePartialModel(
  submission: Record<string, unknown>,
): Record<string, unknown> {
  return {
    confirmCompanyDetails: submission.confirmCompanyDetails,
    fixedPremises: submission.fixedPremises,
    vehicle: submission.vehicle,
    foodBusiness: submission.foodBusiness,
    _Facility: submission._Facility,
  }
}

function generateHash(submission: Record<string, unknown>): string {
  return jsum.digest(
    //need to clone deep becoz jsum corrupts the model somehow
    generatePartialModel(_cloneDeep(submission)),
    'sha256',
    'hex',
  )
}

const activityMap: Record<string, string[]> = {
  'Primary production': [
    'Eggs (of any avian species)',
    'Shellfish (farming of oysters including spat or mussels)',
  ],
  'Food processing/manufacturing': [],
}

function subActivityIsInMap(
  selectedSubActivities: string[],
  subActivityMap: string[],
) {
  return selectedSubActivities.some((value) => subActivityMap.includes(value))
}

function areExportValuesSelected(
  fixedPremise: Record<string, unknown>,
): boolean {
  const selectedFacilityActivities = fixedPremise.fixedFacilityActivities
  const primaryProductionSubform = fixedPremise[
    'NSW_food_licence_application_form_|__primary_production'
  ] as Record<string, unknown> | undefined

  if (!Array.isArray(selectedFacilityActivities)) {
    return false
  }

  for (const [activity, subActivityMap] of Object.entries(activityMap)) {
    if (selectedFacilityActivities.includes(activity)) {
      switch (activity) {
        case 'Food processing/manufacturing':
          return true
        case 'Primary production':
          const selectedSubActivities =
            primaryProductionSubform?.primaryProduction as string[] | undefined

          if (
            selectedSubActivities &&
            subActivityIsInMap(selectedSubActivities, subActivityMap)
          ) {
            return true
          }
      }
    }
  }

  return false
}

const pricingPaths = {
  permissionsPath: '/pricing/food-license/permissions',
  productsPath: '/pricing/food-license/products',
  facilityPath: '/pricing/food-license/facility',
}

export default function FoodLicenceApplicationFormContainer({
  form,
  preFillData,
}: FormProps) {
  const customForm = React.useMemo(() => {
    setHasPricingLookupRunInitial(form, {})
    setSpinnerInitial(form)

    return form
  }, [form])

  const [partialModelHash, setPartialModelHash] = React.useState<string>()

  const [lookupError, setLookupError] = React.useState<string>()
  const [isLookupRunning, setIsLookupRunning] = React.useState(false)

  /*
    `customSetFormSubmission` is handed to every element in the form as its
    `onChange`. Reading these two values from state would put them in its
    dependency list, so the callback would get a new identity whenever either
    changed. Derived-value elements (calculation, summary) re-run their write
    effect on a new `onChange`, and a repeatable set rebuilds the whole entry
    for any write, so a changing identity here is enough to loop the form
    forever. Mirror them into refs and keep the callback stable.
  */
  const partialModelHashRef = React.useRef(partialModelHash)
  const isLookupRunningRef = React.useRef(isLookupRunning)

  const updatePartialModelHash = React.useCallback((hash: string) => {
    partialModelHashRef.current = hash
    setPartialModelHash(hash)
  }, [])

  const updateIsLookupRunning = React.useCallback((running: boolean) => {
    isLookupRunningRef.current = running
    setIsLookupRunning(running)
  }, [])

  const { buttons } = useAppConfig()

  const [onSaveDraft, isSavingDraft] = useSaveDraft({
    formDefinition: form,
    preFillData,
  })

  const [onCancel, cancelError] = useCancelForm(customForm)

  const [isGettingCharges, setIsGettingCharges] = React.useState<boolean>(false)
  const [{ isValidating, serverValidationMessage }, setValidationState] =
    React.useState<{ isValidating: boolean; serverValidationMessage?: string }>(
      { isValidating: false },
    )
  const [{ onSubmit }, { isSubmitting }] = useSubmitForm({
    formDefinition: customForm,
    preFillData,
    skipServerValidation: true,
  })

  const {
    definition,
    liveSubmission,
    setFormSubmission,
    handleSubmit,
    handleCancel,
    handleSaveDraft,
    isAutoSaveSubmissionAvailable,
    isLoadingAutoSaveSubmission,
    startNewSubmission,
    continueAutoSaveSubmission,
    executedLookups,
  } = useAutoSaveApprovalsControlledForm({
    form,
    initialSubmission: preFillData,
    onSubmit: onSubmit,
    onCancel,
    onSaveDraft,
  })

  /* noPicReasonOptions */
  const getNoPicReasonOptionValue = React.useCallback(
    (label: string) => {
      const noPicReasonElement = findElement<FormTypes.SelectElement>(
        definition,
        'No_PIC_Reason_Id',
      )
      const regExp = new RegExp(label)
      if (noPicReasonElement && noPicReasonElement.value) {
        const otherOption = noPicReasonElement.value.options?.find((opt) =>
          regExp.test(opt.label),
        )
        return otherOption?.value as string
      }
    },
    [definition],
  )

  const noPicReasonOtherOptionId: string | undefined = React.useMemo(
    () => getNoPicReasonOptionValue('^Other$'),
    [getNoPicReasonOptionValue],
  )
  const noPicReasonInvalidOptionId: string | undefined = React.useMemo(
    () => getNoPicReasonOptionValue('realise my PIC was invalid$'),
    [getNoPicReasonOptionValue],
  )

  const poBoxRegex = React.useMemo(() => /^po box/i, [])

  /* update definition here */
  const customDefinition = React.useMemo(() => {
    let def = definition
    def = generateLicenceChargesTable(liveSubmission, def)
    def = populateContactElements(liveSubmission, def)
    def = updateSpinnerState(liveSubmission, def)
    def = populateBusinessContactsEmail(liveSubmission, def)

    const addressSameAsMainElements = [
      'IsBaseLocationSameAsPostalAddress?_',
      'Address_For_Service_of_Notices',
      'location_same',
    ]

    for (const address of addressSameAsMainElements) {
      const facilityAddressSameAsMainElement = findElement(def, address)
        ?.value as FormTypes.SelectElement

      if (facilityAddressSameAsMainElement) {
        facilityAddressSameAsMainElement.customCssClasses =
          liveSubmission.hideAddress_For_Service_of_Notices
            ? addClass(
                'hidden',
                facilityAddressSameAsMainElement.customCssClasses,
              )
            : removeClass(
                'hidden',
                facilityAddressSameAsMainElement.customCssClasses,
              )
      }
    }

    if (lookupError) {
      def = setSpinnerError(def, lookupError)
    }

    // pages are memo'd so we need new objects for the updated definition to render
    return { ...def, elements: def.elements.map((el) => ({ ...el })) }
  }, [definition, liveSubmission, lookupError])

  /* update submission data here */
  const customSetFormSubmission: SetFormSubmission = React.useCallback(
    (formSubmission) => {
      setFormSubmission((currentState) => {
        const newFormSubmission: {
          definition: FormTypes.Form
          submission: SubmissionTypes.S3SubmissionData['submission']
          lastElementUpdated: FormTypes.FormElement | undefined
          executedLookups: ExecutedLookups
        } =
          typeof formSubmission === 'function'
            ? formSubmission(currentState)
            : formSubmission
        const { submission, definition } = newFormSubmission
        if (submission.fauxPricingLookup__exclude === 'calculateCharges') {
          if (!isLookupRunningRef.current) {
            updateIsLookupRunning(true)
            setLookupError(undefined)
            getPricing({ submission, definition, ...pricingPaths })
              .then((result) => {
                if (result) {
                  setFormSubmission((prev) => {
                    return {
                      ...prev,
                      submission: {
                        ...prev.submission,
                        ...result,
                        fauxPricingLookup__exclude: undefined,
                      },
                    }
                  })
                }
              })
              .catch((e) => {
                if (e instanceof HTTPError) {
                  e.status === 666
                    ? setLookupError('Failed to calculate pricing')
                    : setLookupError(e.message)
                }
                setFormSubmission((prev) => {
                  return {
                    ...prev,
                    submission: {
                      ...prev.submission,
                      fauxPricingLookup__exclude: undefined,
                      products: undefined,
                      paymentTotal__hidden: undefined,
                      facilities__hidden: undefined,
                      applicationFee__hidden: undefined,
                      applicationFeeTaxRate__hidden: undefined,
                      applicationFeePriorPaymentRequired__hidden: undefined,
                      hasPricingLookupRun__hidden: undefined,
                    },
                  }
                })
                console.error(e)
              })
              .finally(() => {
                updateIsLookupRunning(false)
              })
          }
        }

        let hideAddress_For_Service_of_Notices = false

        if (submission.Manual_Address) {
          const address = ((submission.Address_1 as string) ?? '').trim()
          hideAddress_For_Service_of_Notices = poBoxRegex.test(address)
        } else {
          const address = submission.Address as
            | SubmissionPointAddress
            | undefined
          hideAddress_For_Service_of_Notices =
            (getPointAddressPostalDeliveryType(address) || '').toLowerCase() ===
            'po box'
        }

        submission.hideAddress_For_Service_of_Notices =
          hideAddress_For_Service_of_Notices

        if (
          Array.isArray(submission.fixedPremises) &&
          submission.fixedPremises.length
        ) {
          const fixedPremises = submission.fixedPremises as
            | Array<Record<string, unknown>>
            | undefined
          fixedPremises?.forEach((fixedPremise, index) => {
            const fp =
              fixedPremise.nswFoodLicenceApplicationFormFixedPremises as
                | Record<string, unknown>
                | undefined
            if (fp) {
              fp.areExportValuesSelected = areExportValuesSelected(fp)

              if (hideAddress_For_Service_of_Notices) {
                fp.Address_For_Service_of_Notices = 'No'
              }

              if (fixedPremise.copyFixedPremise) {
                if (Array.isArray(submission.fixedPremises)) {
                  fixedPremise.copyFixedPremise = undefined

                  submission.fixedPremises.splice(index + 1, 0, {
                    ...fixedPremise,
                    facilityName: undefined,
                    nswFoodLicenceApplicationFormFixedPremises: {
                      ...(fixedPremise.nswFoodLicenceApplicationFormFixedPremises as Record<
                        string,
                        unknown
                      >),
                      Trading_Name: undefined,
                      Address_For_Service_of_Notices: undefined,
                      Facility_Address: undefined,
                      Primary_Manual_Address: undefined,
                      Primary_Address_line_1: undefined,
                      Primary_Address_Line_2: undefined,
                      Suburb: undefined,
                      Primary_State: undefined,
                      Postcode: undefined,
                      Facility_Phone: undefined,
                      Avoka_contact_reference: undefined,
                      currentOwnersLicenceNumber: undefined,
                      premiseHasAnotherOwner: undefined,
                      Title: undefined,
                      First_Name: undefined,
                      Last_Name: undefined,
                      Phone: undefined,
                      Email: undefined,
                      Job_Function: undefined,
                    },
                  })

                  toast.success('Facility copied', {
                    position: 'bottom-right',
                    hideProgressBar: true,
                  })
                }
              }
            }
          })
        }

        if (Array.isArray(submission.vehicle)) {
          submission.vehicle.forEach((vehicle) => {
            if (
              Array.isArray(
                vehicle.nswFoodLicenceApplicationFormVehicle
                  .Location_of_vehicles,
              )
            ) {
              vehicle.nswFoodLicenceApplicationFormVehicle.Location_of_vehicles.forEach(
                (location: Record<string, unknown>) => {
                  if (hideAddress_For_Service_of_Notices) {
                    location['location_same'] = 'No'
                  }
                },
              )
            }
          })
        }

        if (Array.isArray(submission.foodBusiness)) {
          submission.foodBusiness.forEach((fishingBusiness) => {
            if (hideAddress_For_Service_of_Notices) {
              const business =
                fishingBusiness.nswFoodLicenceApplicationFormFishing as
                  | Record<string, unknown>
                  | undefined
              if (business) {
                business['IsBaseLocationSameAsPostalAddress?_'] = 'No'
              }
            }
          })
        }

        updateEmergencyContactIsMobile(submission, definition)

        updateFacilityLabels(submission)

        personCompletingFormDetails(submission, definition)

        avokaContactReferenceDetails(submission, definition)

        emergencyContact(submission, definition)

        if (Array.isArray(submission.fixedPremises)) {
          for (const fixedPremise of submission.fixedPremises) {
            const primaryProductionSubForm =
              fixedPremise.nswFoodLicenceApplicationFormFixedPremises?.[
                'NSW_food_licence_application_form_|__primary_production'
              ]
            if (primaryProductionSubForm) {
              primaryProductionSubForm.otherPicReasonSelected__hidden =
                primaryProductionSubForm.valid_Pic === 'No' &&
                primaryProductionSubForm.No_PIC_Reason_Id ===
                  noPicReasonOtherOptionId

              primaryProductionSubForm.invalidPicReasonSelected__hidden =
                primaryProductionSubForm.valid_Pic === 'No' &&
                primaryProductionSubForm.No_PIC_Reason_Id ===
                  noPicReasonInvalidOptionId
            }
          }
        }

        //always do this last
        updateHasPricingLookupRun({
          submission: submission,
          currentHash: partialModelHashRef.current,
          newHash: generateHash(submission),
          updateHash: updatePartialModelHash,
        })

        /*
          Calculation elements re-run and write their result back whenever the
          `onChange` handed to them changes identity, even when the value is
          identical. Returning a new state object for one of those writes gives
          the renderer a new definition and a new `onChange` for every element,
          which runs the calculations again, and the form never settles. Keeping
          the current state when nothing actually changed breaks that cycle.

          `lastElementUpdated` is deliberately not compared: the competing
          calculations report different elements on every pass, so including it
          would mean never finding the states equal. A write that leaves the
          submission untouched has nothing for a lookup to react to anyway.
        */
        if (
          newFormSubmission.definition === currentState.definition &&
          isDeepEqual(submission, currentState.submission) &&
          isDeepEqual(
            newFormSubmission.executedLookups,
            currentState.executedLookups,
          )
        ) {
          return currentState
        }

        return { ...currentState, ...newFormSubmission }
      })
    },
    [
      setFormSubmission,
      updateIsLookupRunning,
      updatePartialModelHash,
      noPicReasonOtherOptionId,
      noPicReasonInvalidOptionId,
      poBoxRegex,
    ],
  )

  const customHandleSubmission = React.useCallback(
    async (newFormSubmission: submissionService.NewFormSubmission) => {
      //taking all the precautions here and overengineering it
      const clonedNewFormSubmission = _cloneDeep(newFormSubmission)
      let pricingCompleted = false
      setValidationState({ isValidating: true })
      let serverValidationMessage: string | undefined = undefined
      try {
        console.log('initial server validation')
        await serverValidation(
          clonedNewFormSubmission.definition.serverValidation,
          clonedNewFormSubmission.submission,
          clonedNewFormSubmission.definition,
        )
      } catch (e) {
        console.log(e)
        serverValidationMessage =
          e instanceof OneBlinkAppsError
            ? e.message
            : 'Unknown error during validation'
      }

      setValidationState({ isValidating: false, serverValidationMessage })

      if (serverValidationMessage) {
        return
      }

      if (
        !verifyPricingLookupHasRun({
          newFormSubmission: clonedNewFormSubmission,
        })
      ) {
        return
      }

      setIsGettingCharges(true)
      try {
        const pricingFunc = () =>
          getPricing({ ...clonedNewFormSubmission, ...pricingPaths })

        const { pricingResponse, customerReferenceNumber } =
          await getPricingAndCrn(pricingFunc, 'NSW Food Authority')

        const facilityLineItems = generateFacilityLineItems(pricingResponse)
        const applicationFeeLineItem =
          generateApplicationFeeLineItem(pricingResponse)

        const productLineItems = generateProductLineItems(pricingResponse)

        clonedNewFormSubmission.submission = {
          ...clonedNewFormSubmission.submission,
          paymentTotal__hidden: pricingResponse.paymentTotal__hidden,
          customerReferenceNumber__hidden: customerReferenceNumber,
          lineItems: [
            ...facilityLineItems,
            applicationFeeLineItem,
            ...productLineItems,
          ],
        }
        pricingCompleted = true
      } catch (e) {
        console.error(e)
        toast.error('There was an error verifying payment amount', {
          position: 'bottom-right',
          hideProgressBar: true,
        })
      }

      setIsGettingCharges(false)

      //a Hail Mary to prevent premature submissions
      if (!pricingCompleted) {
        return
      }

      handleSubmit(clonedNewFormSubmission)
    },
    [handleSubmit],
  )

  const [
    { isFetchingMaintenanceWindow, fetchMaintenanceWindowError },
    { wrappedSubmitFn, clearMaintenanceMessage },
  ] = useCheckMaintenanceOnSubmit(customHandleSubmission)

  return (
    <>
      <AutoSaveApprovalsControlledForm
        definition={customDefinition}
        submission={liveSubmission}
        onCancel={handleCancel}
        onSubmit={wrappedSubmitFn}
        onSaveDraft={handleSaveDraft}
        setFormSubmission={customSetFormSubmission}
        buttons={buttons}
        isSubmitting={
          isSubmitting || isGettingCharges || isFetchingMaintenanceWindow
        }
        cancelError={cancelError}
        isAutoSaveSubmissionAvailable={isAutoSaveSubmissionAvailable}
        isLoadingAutoSaveSubmission={isLoadingAutoSaveSubmission}
        isSavingDraft={isSavingDraft}
        startNewSubmission={startNewSubmission}
        continueAutoSaveSubmission={continueAutoSaveSubmission}
        executedLookups={executedLookups}
        serverValidationMessage={serverValidationMessage}
        clearServerValidationMessage={() =>
          setValidationState({ isValidating: false })
        }
      />
      <Dialog isOpen={isValidating}>
        <LoadingSpinner>Validating</LoadingSpinner>
      </Dialog>
      <MaintenanceModal
        fetchMaintenanceWindowError={fetchMaintenanceWindowError}
        clearMaintenanceMessage={clearMaintenanceMessage}
      />
    </>
  )
}
